import React from "react";

export function Modal({
  title,
  onClose,
  children,
  wide,
  testId,
}: {
  title: string;
  onClose: () => void;
  children: React.ReactNode;
  wide?: boolean;
  testId?: string;
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-green-deep/40 p-4 backdrop-blur-sm" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div data-testid={testId || "modal"} className={`nx-modal-in nx-card mt-10 w-full ${wide ? "max-w-4xl" : "max-w-lg"} p-5`}>
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-lg font-bold text-green">{title}</h2>
          <button data-testid="modal-close-btn" onClick={onClose} className="rounded-lg p-1.5 text-muted hover:bg-soft hover:text-ink">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 6 6 18M6 6l12 12" /></svg>
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

export function Field({ label, children, className }: { label: string; children: React.ReactNode; className?: string }) {
  return (
    <div className={className}>
      <label className="nx-label">{label}</label>
      {children}
    </div>
  );
}

export function Badge({ children, className, testId }: { children: React.ReactNode; className?: string; testId?: string }) {
  return (
    <span data-testid={testId} className={`nx-badge ${className || "bg-soft text-green"}`}>
      {children}
    </span>
  );
}

export function EmptyState({ message }: { message: string }) {
  return (
    <div data-testid="empty-state" className="flex flex-col items-center gap-2 py-12 text-center text-muted">
      <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="text-line">
        <rect x="3" y="4" width="18" height="16" rx="2" /><path d="M3 9h18" />
      </svg>
      <p className="text-sm">{message}</p>
    </div>
  );
}

export function Spinner() {
  return (
    <div className="flex justify-center py-16" data-testid="spinner">
      <div className="h-8 w-8 animate-spin rounded-full border-[3px] border-line border-t-green" />
    </div>
  );
}

export function Confirm({
  message,
  onConfirm,
  onCancel,
}: {
  message: string;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  return (
    <Modal title="Confirmation" onClose={onCancel} testId="confirm-modal">
      <p className="mb-5 text-sm text-ink">{message}</p>
      <div className="flex justify-end gap-2">
        <button data-testid="confirm-cancel-btn" className="nx-btn-ghost" onClick={onCancel}>
          Annuler
        </button>
        <button data-testid="confirm-ok-btn" className="nx-btn-danger" onClick={onConfirm}>
          Confirmer
        </button>
      </div>
    </Modal>
  );
}

export function Tabs({
  tabs,
  active,
  onChange,
}: {
  tabs: { key: string; label: string; count?: number }[];
  active: string;
  onChange: (k: string) => void;
}) {
  return (
    <div className="mb-4 flex gap-1 rounded-xl border border-line bg-white p-1">
      {tabs.map((t) => (
        <button
          key={t.key}
          data-testid={`tab-${t.key}`}
          onClick={() => onChange(t.key)}
          className={`flex-1 rounded-lg px-3 py-2 text-sm font-semibold transition-colors ${
            active === t.key ? "bg-green text-white" : "text-muted hover:bg-soft"
          }`}
        >
          {t.label}
          {t.count !== undefined && <span className="ml-1.5 text-xs opacity-70">({t.count})</span>}
        </button>
      ))}
    </div>
  );
}
